import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import Form from 'components/common/formElements/form';
import moment, { Moment } from 'moment';
import Switch from 'react-switch';
import buttons from 'style/buttons.module.scss';
import Title from 'components/common/Title';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import moduleStyles from 'pages/timetable/styles.module.scss';
import {
  AvailablePaymentMethod,
  AvailablePlan,
  ChosenPaymentMethod,
} from 'reducers/payments';
import { UserSubscriptionStatus } from 'types';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import ArrowIcon from 'components/common/calendar/arrowIcon';
import {
  getFormattedCardNumber,
  isSubscriptionToBeCanceled,
} from 'utils/helpers/payments';
import { Trackable } from 'utils/hooks/useTrackable';
import { selectAvailablePlans, selectReferralInfo } from 'selectors/payments';
import { TimeUnit } from 'utils/constants/time';
import styles from './index.module.scss';

const useStyles = makeStyles(() =>
  createStyles({
    formControl: {
      margin: '0px ',
      minWidth: 120,
      width: '100%',
      height: 52,
      border: '1px solid #E8E7EC',
      borderRadius: 100,
    },
  })
);

interface Props {
  onSubscribe: Trackable<
    (params: {
      card: ChosenPaymentMethod;
      autoRenewOn: boolean;
    }) => Promise<void>
  >;
  onRetry: Trackable<(params: { card: ChosenPaymentMethod }) => Promise<void>>;
  onChangeSubscription: Trackable<
    (params: {
      card: ChosenPaymentMethod;
      autoRenewOn: boolean;
    }) => Promise<void>
  >;
  onBack: () => void;
  availableCards: AvailablePaymentMethod[];
  subscriptionStatus?: UserSubscriptionStatus;
  selectedPlan: AvailablePlan | undefined;
  currentPlanId: string;
  currentSubscriptionEnd: number;
}

const Subscribing: FC<Props> = ({
  onSubscribe,
  onRetry,
  onChangeSubscription,
  onBack,
  availableCards,
  subscriptionStatus,
  selectedPlan,
  currentPlanId,
  currentSubscriptionEnd,
}) => {
  const classes = useStyles();
  const availablePlans = useSelector(selectAvailablePlans);
  const referralInfo = useSelector(selectReferralInfo);
  const [checked, setChecked] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  const isUnpaidSubscription =
    subscriptionStatus === UserSubscriptionStatus.UNPAID;

  const activePlan: AvailablePlan | undefined = useMemo(
    () => availablePlans.find((plan) => plan.id === currentPlanId),
    [availablePlans, currentPlanId]
  );

  const isActivePlanSelected =
    selectedPlan && activePlan && selectedPlan.id === activePlan.id;

  const formatYear = (data: Moment) => data.format('DD-MM-YYYY');

  const expiresOnMoment = formatYear(
    moment.utc(currentSubscriptionEnd * TimeUnit.Second)
  );

  const startMoment = formatYear(
    moment
      .utc(currentSubscriptionEnd * TimeUnit.Second)
      .subtract(1, activePlan?.interval)
  );

  const newSubscriptionStartMoment = expiresOnMoment;

  const newSubscriptionExpiresMoment = formatYear(
    moment
      .utc(currentSubscriptionEnd * TimeUnit.Second)
      .add(1, selectedPlan?.interval)
  );

  const availableCardsMap = useMemo(() => {
    const result: Record<string | 'default', AvailablePaymentMethod> = {};

    availableCards.forEach((card) => {
      result[card.id] = card;

      if (card.default) {
        result.default = card;
      }
    });

    return result;
  }, [availableCards]);

  useLayoutEffect(() => {
    if (!selectedCardId && availableCardsMap.default) {
      setSelectedCardId(availableCardsMap.default.id);
    }
  }, [availableCardsMap, selectedCardId]);

  const getSelectedCard = (): ChosenPaymentMethod => {
    return availableCardsMap[selectedCardId] || { isNew: true };
  };

  const handleSubscribe = (event: React.MouseEvent) => {
    event.preventDefault();
    onSubscribe({ card: getSelectedCard(), autoRenewOn: checked });
  };

  const handleRetry = (event: React.MouseEvent) => {
    event.preventDefault();
    onRetry({ card: getSelectedCard() });
  };

  const handleChangeSubscription = (event: React.MouseEvent) => {
    event.preventDefault();
    onChangeSubscription({ card: getSelectedCard(), autoRenewOn: checked });
  };

  const handleNewCardSelect = () => {
    setSelectedCardId('new-card');
  };

  const handleExistingCardSelect = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setSelectedCardId(event.target.value as string);
  };

  const renderActionButton = () => {
    const renderButton = (props: {
      onClick: React.MouseEventHandler;
      children: string;
      disabled: boolean;
    }) => <button type="submit" className={buttons.buttomPrimary} {...props} />;

    if (isSubscriptionToBeCanceled(subscriptionStatus)) {
      return null;
    }

    if (isUnpaidSubscription && isActivePlanSelected) {
      return renderButton({
        onClick: handleRetry,
        children: onRetry.inProgress ? 'Retrying...' : 'Retry',
        disabled: onRetry.inProgress,
      });
    }

    if (activePlan && !isActivePlanSelected) {
      return renderButton({
        onClick: handleChangeSubscription,
        children: onChangeSubscription.inProgress ? 'Changing...' : 'Change',
        disabled: onChangeSubscription.inProgress,
      });
    }

    if (!activePlan) {
      return renderButton({
        onClick: handleSubscribe,
        children: onSubscribe.inProgress ? 'Subscribing...' : 'Subscribe',
        disabled: onSubscribe.inProgress,
      });
    }

    return null;
  };

  const renderReferralBonusExplanation = () => {
    if (
      !referralInfo ||
      referralInfo?.bonusesReceived >= referralInfo?.requestsAccepted ||
      selectedPlan?.interval !== 'month' ||
      (isActivePlanSelected && !isUnpaidSubscription)
    ) {
      return null;
    }

    let text;
    if (availableCards.length) {
      text =
        'You have a referral bonus. It will be used as payment for the next transaction.';
    } else {
      text =
        'You have a referral bonus, to use it, please provide your payment information.';
    }

    return <div className={styles.informSection}>{text}</div>;
  };

  return (
    <>
      <div className={styles.subscribing}>
        <div>
          <div className={`${styles.subscriptionDetails} ${styles.mainBlock}`}>
            <Title>Confirm Your Subscription Details</Title>
            <div className={styles.table}>
              <div>
                <div>Details</div>
                <div />
              </div>
              <div>
                <div>Subscription type</div>
                <div>{selectedPlan?.name}</div>
              </div>
              <div>
                <div>Billing period</div>
                <div>
                  {isActivePlanSelected
                    ? `${startMoment} to ${expiresOnMoment}`
                    : `${newSubscriptionStartMoment} to ${newSubscriptionExpiresMoment}`}
                </div>
              </div>
              <div>
                <div>Expires/Renews on</div>
                <div>
                  {isActivePlanSelected
                    ? expiresOnMoment
                    : newSubscriptionExpiresMoment}
                </div>
              </div>
              <div>
                <div>Total billed today</div>
                <div>
                  {selectedPlan?.name === 'Yearly plan' ? '$60.00' : '$6.99'}
                </div>
              </div>
            </div>
            <div className={styles.swither}>
              <span>Auto-renew</span>
              <Switch
                onChange={() => setChecked((state) => !state)}
                checked={checked}
                uncheckedIcon={false}
                checkedIcon={false}
                offColor="#8D869E"
                onColor="#4998ff"
              />
            </div>
            <span>
              The subscription will auto-renew around [Expires/Renews date]
              every [month or year]. You will automatically be billed when the
              renewal occurs.
            </span>
          </div>
          <div className={`${styles.paymentForm} ${styles.mainBlock}`}>
            <Title>Payment Details</Title>
            {!_.isEmpty(availableCards) && (
              <FormControl
                className={`${classes.formControl} ${styles.formInput}`}
              >
                <Select
                  value={selectedCardId}
                  onChange={handleExistingCardSelect}
                  inputProps={{ 'aria-label': 'Without label' }}
                  IconComponent={ArrowIcon}
                  displayEmpty
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    classes: { paper: styles.formSelect },
                    getContentAnchorEl: null,
                  }}
                  placeholder="Choose default card"
                  renderValue={(selected: unknown) =>
                    availableCardsMap[selected as string] ? (
                      getFormattedCardNumber(
                        availableCardsMap[selected as string]
                      )
                    ) : (
                      <div>Choose default card</div>
                    )
                  }
                >
                  {availableCards.map((card) => (
                    <MenuItem key={card.id} value={card.id}>
                      {getFormattedCardNumber(card)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Form>
              <CardNumberElement
                onChange={handleNewCardSelect}
                onFocus={handleNewCardSelect}
                className={styles.cardInput}
                options={{
                  placeholder: 'Card number',
                  style: {
                    base: {
                      lineHeight: '50px',
                    },
                  },
                }}
              />
              <CardExpiryElement
                onChange={handleNewCardSelect}
                onFocus={handleNewCardSelect}
                className={`${styles.cardInput} ${styles.minWidth}`}
                options={{
                  style: {
                    base: {
                      lineHeight: '50px',
                    },
                  },
                }}
              />
              <CardCvcElement
                onChange={handleNewCardSelect}
                onFocus={handleNewCardSelect}
                className={`${styles.cardInput} ${styles.minWidth}`}
                options={{
                  placeholder: 'Security code',
                  style: {
                    base: {
                      lineHeight: '50px',
                    },
                  },
                }}
              />
            </Form>
            {renderReferralBonusExplanation()}
          </div>
        </div>
      </div>
      <div
        className={`${moduleStyles.buttons} ${styles.bottomActions} ${styles.subscribeBottom}`}
      >
        <button
          onClick={onBack}
          className={buttons.buttonOutline}
          type="button"
        >
          Back
        </button>
        {renderActionButton()}
      </div>
    </>
  );
};

export default Subscribing;
